import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Camera, CameraOff, AlertTriangle, Eye, EyeOff, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

let blazefaceModel = null;

// Mobile detection: lighten AI monitoring load on phones/tablets
const IS_MOBILE = typeof navigator !== 'undefined' && (
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  (typeof window !== 'undefined' && window.innerWidth < 768)
);
const DETECT_INTERVAL = IS_MOBILE ? 1000 : 500;
const DETECT_INPUT_WIDTH = 160;

async function loadModel() {
  if (blazefaceModel) return blazefaceModel;
  const tf = await import('@tensorflow/tfjs');
  try {
    await tf.setBackend('webgl');
  } catch (e) {
    // fall back to default backend
  }
  await tf.ready();
  const blazeface = await import('@tensorflow-models/blazeface');
  blazefaceModel = await blazeface.load();
  return blazefaceModel;
}

export function useFaceMonitor({ onFaceLost, onFaceFound, enabled = true, graceSeconds = 5 }) {
  const { t } = useLanguage();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [faceDetected, setFaceDetected] = useState(true);
  const [cameraError, setCameraError] = useState(null);
  const [monitoring, setMonitoring] = useState(false);
  const [warningVisible, setWarningVisible] = useState(false);
  
  const streamRef = useRef(null);
  const detectionRef = useRef(null);
  const detectCanvasRef = useRef(null);
  const lostTimerRef = useRef(null);
  const faceStateRef = useRef(true);
  const consecutiveMissRef = useRef(0);

  const startCamera = useCallback(async () => {
    if (!enabled) return;
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 320, height: 240, facingMode: 'user' } 
      });
      streamRef.current = stream;
      setCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.name === 'NotAllowedError' 
        ? t('faceMonitor.cameraAccessDenied')
        : t('faceMonitor.cameraStartFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Attach stream to video element after CameraPreview mounts
  useEffect(() => {
    if (!cameraActive || !streamRef.current) return;
    const attachStream = async () => {
      // Wait for video ref to be available after render
      const waitForRef = () => new Promise((resolve) => {
        const check = () => {
          if (videoRef.current) return resolve();
          requestAnimationFrame(check);
        };
        check();
      });
      await waitForRef();
      if (videoRef.current && streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        await videoRef.current.play();
        try {
          const model = await loadModel();
          setModelLoaded(true);
          setMonitoring(true);
          startDetectionLoop(model);
        } catch (err) {
          console.error('Model load error:', err);
        }
      }
    };
    attachStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraActive]);


  const stopCamera = useCallback(() => {
    if (detectionRef.current) {
      cancelAnimationFrame(detectionRef.current);
      detectionRef.current = null;
    }
    if (lostTimerRef.current) {
      clearTimeout(lostTimerRef.current);
      lostTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setMonitoring(false);
    setWarningVisible(false);
  }, []);

  const startDetectionLoop = useCallback((model) => {
    const detect = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) {
        detectionRef.current = requestAnimationFrame(detect);
        return;
      }

      try {
        // C: downscale the frame to a small canvas to cut CPU/GPU load
        const v = videoRef.current;
        let input = v;
        try {
          if (!detectCanvasRef.current) detectCanvasRef.current = document.createElement('canvas');
          const cv = detectCanvasRef.current;
          const srcW = v.videoWidth || 320;
          const srcH = v.videoHeight || 240;
          const scale = DETECT_INPUT_WIDTH / srcW;
          cv.width = DETECT_INPUT_WIDTH;
          cv.height = Math.max(1, Math.round(srcH * scale));
          cv.getContext('2d').drawImage(v, 0, 0, cv.width, cv.height);
          input = cv;
        } catch (e) {
          input = v;
        }
        const predictions = await model.estimateFaces(input, false);
        const hasFace = predictions.length > 0;

        if (hasFace) {
          consecutiveMissRef.current = 0;
          if (!faceStateRef.current) {
            faceStateRef.current = true;
            setFaceDetected(true);
            setWarningVisible(false);
            if (lostTimerRef.current) {
              clearTimeout(lostTimerRef.current);
              lostTimerRef.current = null;
            }
            onFaceFound?.();
          }
        } else {
          consecutiveMissRef.current++;
          // Require 3 consecutive misses to reduce false positives
          if (consecutiveMissRef.current >= 3 && faceStateRef.current) {
            faceStateRef.current = false;
            setFaceDetected(false);
            // Start grace period
            if (!lostTimerRef.current) {
              lostTimerRef.current = setTimeout(() => {
                if (!faceStateRef.current) {
                  setWarningVisible(true);
                  onFaceLost?.();
                }
                lostTimerRef.current = null;
              }, graceSeconds * 1000);
            }
          }
        }
      } catch (e) {
        // Ignore detection errors
      }

      // Run detection at a device-appropriate interval to save CPU (mobile slower)
      setTimeout(() => {
        detectionRef.current = requestAnimationFrame(detect);
      }, DETECT_INTERVAL);
    };

    detectionRef.current = requestAnimationFrame(detect);
  }, [onFaceLost, onFaceFound, graceSeconds]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Stable ref callback - attaches stream whenever video element mounts/remounts
  const videoCallbackRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  const CameraPreview = useMemo(() => {
    const Preview = ({ className = '' }) => (
      <div className={`relative ${className}`}>
        <video
          ref={videoCallbackRef}
          className="w-full h-full object-cover rounded-lg transform scale-x-[-1]"
          autoPlay
          muted
          playsInline
        />
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
    return Preview;
  }, [videoCallbackRef]);

  const StatusBadge = ({ }) => cameraActive ? (
    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
      faceDetected ? 'bg-green-500/80 text-white' : 'bg-red-500/80 text-white animate-pulse'
    }`}>
      {faceDetected ? <Eye className="h-2.5 w-2.5" /> : <EyeOff className="h-2.5 w-2.5" />}
      {faceDetected ? 'OK' : t('faceMonitor.notDetected')}
    </div>
  ) : null;

  const WarningOverlay = ({ onResume }) => {
    if (!warningVisible) return null;
    return (
      <div className="absolute inset-0 bg-black/85 flex items-center justify-center z-50" data-testid="face-warning-overlay">
        <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
          <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="h-10 w-10 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('faceMonitor.faceNotDetectedTitle')}</h3>
          <p className="text-gray-600 mb-6">
            {t('faceMonitor.faceNotDetectedDesc')}<br/>
            {t('faceMonitor.videoPaused')}
          </p>
          <button
            onClick={() => {
              setWarningVisible(false);
              onResume?.();
            }}
            className="bg-[#00A651] hover:bg-[#008f45] text-white font-medium px-8 py-3 rounded-lg transition-colors"
            data-testid="face-warning-resume-btn"
          >
            {t('faceMonitor.resume')}
          </button>
        </div>
      </div>
    );
  };

  return {
    startCamera,
    stopCamera,
    cameraActive,
    modelLoaded,
    faceDetected,
    cameraError,
    monitoring,
    warningVisible,
    CameraPreview,
    StatusBadge,
    WarningOverlay,
  };
}
